/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useState } from 'react'
import RulesFilter from 'components/molecules/RulesFilter/RulesFilter'
import Pagination from 'components/molecules/Pagination/Pagination'
import { useSelector } from 'react-redux'
import { RootState } from 'store/store'
import { criteriasList, deleteRuleById, rulesList } from 'services/rulesService'
import { CriteriaList, Filter, Rule } from 'interfaces/rule'
import { LoaderTransparent } from 'components/molecules/LoaderTransparent/LoaderTransparent'
import { Box } from '../../atoms/AppBox'
import { AccordionRuleList } from 'components/molecules/AccordionRuleList/AccordionRuleList'
import ImageEmptyList from 'components/molecules/ImageEmptyList/ImageEmptyList'
import EmptyBox from '../../../assets/empty-box.png'
import { useLocation, useNavigate } from 'react-router-dom'
import AppModalGeneral from 'components/atoms/AppModalGeneral/AppModalGeneral'
import AppButton from 'components/atoms/AppButton/AppButton'
import StatusToast from 'components/molecules/StatusToast/StatusToast'
import { usePermissions } from 'hooks/usePermissions'
import { RolePermissions } from 'utils/constants'

export default function RulesList() {
    const navigate = useNavigate()
    const location = useLocation()
    const { can } = usePermissions()
    const { apiToken } = useSelector((state: RootState) => state.auth)

    const [criterias, setCriterias] = useState<CriteriaList[]>([])
    const [loading, setLoading] = useState(false)
    const [filters, setFilters] = useState<Filter>({
        name: '',
        criteria_types: [],
        page: 1,
    })
    const [pagination, setPagination] = useState({
        totalItems: 0,
        page: 1,
        itemsPerPage: 10,
    })
    const [rules, setRules] = useState([])

    const [deleteSuccess, setDeleteSuccess] = useState(false)
    const [ruleRemoved, setRuleRemoved] = useState<any>(null)
    const [messageError, setMessageError] = useState(
        location.state?.message.message || ''
    )
    const [type, setType] = useState<'error' | 'success' | 'loading'>(
        location.state?.message.type || 'error'
    )

    const fetchData = async (filters: Filter) => {
        setLoading(true)
        const rules = await rulesList(filters, apiToken)
        setRules(rules.items)
        setPagination({
            page: rules.page,
            totalItems: rules.totalItems,
            itemsPerPage: rules.size,
        })
        setLoading(false)
    }

    const updateFilters = (newFilters: Filter) => {
        setFilters((prevFilters) => ({
            ...prevFilters,
            ...newFilters,
        }))
        fetchData(newFilters)
    }

    const changePage = (page: number) => {
        const newFilters = { ...filters, page }
        setFilters(newFilters)
        setPagination({ ...pagination, page })
        fetchData(newFilters)
    }

    const deleteRule = async (rule: Rule) => {
        try {
            setLoading(true)

            const response = await deleteRuleById(rule.id)
            const data = response

            setRuleRemoved(data)

            if (data?.templates?.length > 0) {
                setDeleteSuccess(true)
            }

            setType('success')
            setMessageError('Regla eliminada satisfactoriamente')
            fetchData(filters)
        } catch (error) {
            setType('error')
            setMessageError(
                typeof error === 'object' &&
                    error !== null &&
                    'message' in error
                    ? String((error as { message: unknown }).message)
                    : 'Error al eliminar la regla'
            )
        }
        setLoading(false)
    }

    useEffect(() => {
        if (messageError) {
            setTimeout(() => {
                setMessageError('')
            }, 3000)
        }
    }, [messageError])

    useEffect(() => {
        const fetchCriterias = async () => {
            setLoading(true)
            const criterias = await criteriasList()
            setCriterias(criterias.data)
            setLoading(false)
        }
        fetchCriterias()
    }, [])

    return (
        <div className="">
            {messageError && <StatusToast title={messageError} type={type} />}

            <AppModalGeneral isOpen={deleteSuccess}>
                <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md mx-4 pointer-events-auto">
                    <div className="flex justify-between items-center px-6 py-4 bg-[#F1F5F9]">
                        <h2 className="text-xl font-bold text-[#141A1F]">
                            Plantillas afectadas al eliminar regla
                        </h2>
                        <img
                            src={require('../../../assets/Close.png')}
                            alt="Close"
                            className="img-logo cursor-pointer"
                            onClick={() => setDeleteSuccess(false)}
                        />
                    </div>
                    <hr className="border-gray-200" />
                    {ruleRemoved?.templates?.length > 0 && (
                        <div>
                            <div className="px-6 py-6">
                                <p className="text-base text-center text-gray-700 leading-relaxed font-medium">
                                    {ruleRemoved.templates.map(
                                        (template: string, index: number) => (
                                            <span key={index} className="block">
                                                {template}
                                            </span>
                                        )
                                    )}
                                </p>
                            </div>
                            <div className="flex justify-end gap-3 px-6 pb-6">
                                <AppButton
                                    title="Aceptar"
                                    onClick={() => setDeleteSuccess(false)}
                                    state="primary"
                                    size="sm"
                                    className="bg-[#00B894] text-white font-semibold"
                                />
                            </div>
                        </div>
                    )}
                </div>
            </AppModalGeneral>

            <Box>
                {loading && <LoaderTransparent />}
                <div>
                    {criterias.length > 0 && (
                        <RulesFilter
                            criterias={criterias}
                            onChange={(f) => updateFilters(f)}
                        />
                    )}
                </div>
                <div>
                    <div className="bg-[#F1F5F9] px-[12px] py-2 flex justify-between items-center rounded-xl mt-6">
                        <p className="text-sm font-normal text-[#141A1F] w-2/3">
                            Nombre
                        </p>
                        <p className="text-sm font-normal text-[#141A1F] w-1/3">
                            Warehouse
                        </p>
                        <p className="text-sm font-normal text-[#141A1F] w-1/3 text-right">
                            Criterios
                        </p>
                    </div>
                    <div
                        className="overflow-y-auto"
                        style={{ height: '500px' }}
                    >
                        {rules.length > 0 ? (
                            <div className="flex justify-center py-3 text-sm">
                                <AccordionRuleList
                                    onEdit={(id) => {
                                        navigate('/rules/edit/' + id)
                                    }}
                                    rules={rules}
                                    deleteRule={(rule) => deleteRule(rule)}
                                />
                            </div>
                        ) : (
                            <div className="flex justify-center p-4 text-sm">
                                {can(RolePermissions.rules.create) ? (
                                     <ImageEmptyList
                                    image={EmptyBox}
                                    title="Aún no hay ninguna regla"
                                    description="Crea una o más reglas para listar"
                                    textButton="Crear regla"
                                    onClick={() => navigate('/rules/create')}
                                />
                                ) : (<ImageEmptyList
                                    image={EmptyBox}
                                    title="Aún no hay ninguna regla"
                                    description="Crea una o más reglas para listar"
                                    
                                />)}
                               
                            </div>
                        )}
                    </div>
                </div>
                <div className="flex justify-center mt-10">
                    <Pagination
                        totalPages={Math.ceil(
                            pagination.totalItems / pagination.itemsPerPage
                        )}
                        currentPage={pagination.page}
                        onPageChange={changePage}
                    />
                </div>
            </Box>
        </div>
    )
}
